<script lang="ts">
  import PanelTopBarLayout from "/@/lib/components/PanelTopBarLayout.svelte"
  import { selectedCustomerAccount } from "/@/stores/CustomerAccount"
  import AwaitItemsResponse from "/@lib/components/loading/AwaitItemsResponse.svelte"
  import TreeTable from "/@lib/components/table/TreeTable.svelte"
  import { TABLE_STORAGE_KEYS } from "/@lib/components/table/column-layout"
  import { twApiClient } from "/@lib/tastyworks-rest"
  import type { ColumnDefinitions } from "/@lib/types/Table"
  import { formatReceivedTime } from "/@lib/utils/formats"
  import { PositionRow } from "/@lib/utils/positionRow"
  import {
    comparePositions,
    compareWithinSymbol,
  } from "/@lib/utils/positionRowSort"
  import {
    ItemsResponse,
    type AdminMarketData,
    type AdminPositionParams,
    type CustomerAccountRowResult,
  } from "@tastyworks/tastyworks-api"
  import { Button, Checkbox, Input } from "@tastyworks/ui-library"
  import { chunk, debounce } from "lodash-es"

  // Market-data requests are batched to avoid request failures when an
  // account has many instruments. Lower this if the service still rejects.
  const MARKET_DATA_BATCH_SIZE = 200

  const POSITIONS_COLUMNS: ColumnDefinitions = [
    {
      name: "Symbol",
      dataFormatter: (positionRow: PositionRow) => positionRow.underlyingSymbol,
    },
    {
      name: "Bid",
      dataFormatter: (positionRow: PositionRow) => positionRow.bid,
      sortable: false,
      style: "text-right",
    },
    {
      name: "Ask",
      dataFormatter: (positionRow: PositionRow) => positionRow.ask,
      sortable: false,
      style: "text-right",
    },
    {
      name: "Last",
      dataFormatter: (positionRow: PositionRow) => positionRow.last,
      sortable: false,
      style: "text-right",
    },
    {
      name: "Quantity",
      dataFormatter: (positionRow: PositionRow) => positionRow.quantity,
      comparator: (positionRow: PositionRow) => Number(positionRow.quantity),
      style: "text-right",
    },
    {
      name: "Strike",
      dataFormatter: (positionRow: PositionRow) =>
        isNaN(positionRow.strikePrice) ? "" : positionRow.strikePrice,
      style: "text-right",
    },
    {
      name: "Expiration",
      dataFormatter: (positionRow: PositionRow) =>
        formatReceivedTime(positionRow.expirationDate),
      comparator: (positionRow: PositionRow) => {
        return positionRow.expirationDate
          ? new Date(positionRow.expirationDate)
          : new Date()
      },
    },
    {
      name: "DTE",
      dataFormatter: (positionRow: PositionRow) => positionRow.daysToExpiration,
      style: "text-right",
    },
    {
      name: "C/P/STK",
      dataFormatter: (positionRow: PositionRow) => positionRow.callPutStock,
    },
    {
      name: "Cost/Unit",
      dataFormatter: (positionRow: PositionRow) =>
        `${positionRow.costPerUnit.value} ${positionRow.costPerUnit.direction}`,
      comparator: (positionRow: PositionRow) =>
        (positionRow.position.costEffect === "Debit" ? -1 : 1) *
        Number(positionRow.costPerUnit.value),
      style: "text-right",
    },
  ]

  let positionRowsPromise: Promise<ItemsResponse<PositionRow> | null> = $state(
    Promise.resolve(null)
  )

  function loadGroupBySymbol(): boolean {
    try {
      const raw = localStorage.getItem(
        TABLE_STORAGE_KEYS.positionsGroupBySymbol
      )
      return raw === null ? true : raw === "true"
    } catch {
      return true
    }
  }

  let groupBySymbol = $state(loadGroupBySymbol())
  let symbolSearch = $state("")

  $effect(() => {
    try {
      localStorage.setItem(
        TABLE_STORAGE_KEYS.positionsGroupBySymbol,
        String(groupBySymbol)
      )
    } catch {
      /* quota / private mode */
    }
  })

  let setTreeTableExpanded: (expanded: boolean) => void = $state(
    () => undefined
  )

  const handleSymbolSearchChange = debounce(() => {
    positionRowsPromise = searchPositions($selectedCustomerAccount, {
      symbol: symbolSearch.toUpperCase() || undefined,
    })
  }, 300)

  async function searchPositions(
    selectedCustomerAccount: CustomerAccountRowResult,
    params?: AdminPositionParams
  ) {
    if (!selectedCustomerAccount.account) {
      return Promise.reject(new Error("No account selected"))
    }

    const positions = await twApiClient.AdminPositionsService.index(
      selectedCustomerAccount.account?.accountNumber ?? "",
      params ?? {}
    )

    const symbolBatches = chunk(
      positions.items.map((p) => p.symbol),
      MARKET_DATA_BATCH_SIZE
    )
    const marketDataResponses = await Promise.all(
      symbolBatches.map((batch) =>
        twApiClient.AdminMarketDataService.index(batch)
      )
    )

    const marketDataMap = new Map<string, AdminMarketData>()
    for (const response of marketDataResponses) {
      for (const m of response.items) {
        marketDataMap.set(m.symbol, m)
      }
    }

    const result: ItemsResponse<PositionRow> = new ItemsResponse()
    result.items = positions.items
      .map((position) => {
        const marketDataItem = marketDataMap.get(position.symbol)
        return new PositionRow(position, marketDataItem)
      })
      .sort(comparePositions)

    return result
  }

  $effect(() => {
    positionRowsPromise = searchPositions($selectedCustomerAccount)
  })
</script>

<PanelTopBarLayout>
  <div class="flex items-center gap-3">
    <Button
      variant="secondary"
      onclick={() =>
        (positionRowsPromise = searchPositions($selectedCustomerAccount))}
      >Refresh</Button
    >
    <div class="fit-content inline-block w-fit whitespace-nowrap">
      <Checkbox bind:checked={groupBySymbol} label="Group by Symbol" />
    </div>
    <Button
      variant="secondary"
      disabled={!groupBySymbol}
      onclick={() => setTreeTableExpanded(true)}>Expand All</Button
    >
    <Button
      variant="secondary"
      disabled={!groupBySymbol}
      onclick={() => setTreeTableExpanded(false)}>Collapse All</Button
    >
  </div>
  <Input
    type="search"
    data-testid="symbol-search-input"
    class="min-w-19 max-w-36 bg-white uppercase"
    placeholder="Symbol"
    oninput={handleSymbolSearchChange}
    bind:value={symbolSearch}
  />
</PanelTopBarLayout>

<div class="flex h-0 grow border-2">
  <AwaitItemsResponse
    promise={positionRowsPromise}
    noResultsMessage="No positions found"
  >
    {#snippet children({ items }: { items: PositionRow[] })}
      <TreeTable
        {items}
        columns={POSITIONS_COLUMNS}
        bind:setAllRowsExpanded={setTreeTableExpanded}
        grouped={groupBySymbol}
        withinGroupSort={compareWithinSymbol}
        defaultSort={{ column: "Symbol" }}
        storageKey={TABLE_STORAGE_KEYS.positions}
      />
    {/snippet}
  </AwaitItemsResponse>
</div>
